import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { container } from '@/app/container';
import { GuaranteeRepositoryToken } from '../../di/tokens';
import type {
  CampaignOfferRef,
  GuaranteeRule,
} from '../../domain/entities/guarantee.entity';

const CAMPAIGNS_KEY = ['guarantee', 'campaigns'];
/**
 * Criar e renomear, para o modal saber que há gravação em voo: fechado no meio,
 * o form desmonta e o `onSuccess` dele (ir ao detalhe, fechar) não roda.
 */
export const CAMPAIGN_CREATE_KEY = ['guarantee', 'campaigns', 'create'];
/** Por campanha: o renomear em voo de uma não trava o modal de outra. */
export const campaignRenameKey = (id: string) => [
  'guarantee',
  'campaigns',
  'rename',
  id,
];

export function useGuaranteeCampaigns(page: number, perPage: number) {
  const repository = container.resolve(GuaranteeRepositoryToken);
  return useQuery({
    queryKey: [...CAMPAIGNS_KEY, 'list', page, perPage],
    queryFn: () => repository.listCampaigns({ page, perPage }),
    placeholderData: keepPreviousData,
  });
}

export function useGuaranteeCampaign(id: string) {
  const repository = container.resolve(GuaranteeRepositoryToken);
  return useQuery({
    queryKey: [...CAMPAIGNS_KEY, 'detail', id],
    queryFn: () => repository.getCampaign(id),
  });
}

/** Toda mutação mexe na lista (contagens) e no detalhe: invalida o prefixo. */
function useInvalidateCampaigns() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: CAMPAIGNS_KEY });
}

export function useCreateGuaranteeCampaign() {
  const repository = container.resolve(GuaranteeRepositoryToken);
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationKey: CAMPAIGN_CREATE_KEY,
    mutationFn: (input: { name: string; rule: GuaranteeRule }) =>
      repository.createCampaign(input),
    onSuccess: invalidate,
  });
}

export function useRenameGuaranteeCampaign(id: string) {
  const repository = container.resolve(GuaranteeRepositoryToken);
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationKey: campaignRenameKey(id),
    mutationFn: (name: string) => repository.renameCampaign(id, name),
    onSuccess: invalidate,
  });
}

export function useAddGuaranteeCampaignOffer(id: string) {
  const repository = container.resolve(GuaranteeRepositoryToken);
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (offer: CampaignOfferRef) => repository.addOffer(id, offer),
    onSuccess: invalidate,
  });
}

export function useRemoveGuaranteeCampaignOffer(id: string) {
  const repository = container.resolve(GuaranteeRepositoryToken);
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (offer: CampaignOfferRef) => repository.removeOffer(id, offer),
    onSuccess: invalidate,
  });
}

export function useUpdateGuaranteeCampaignRule(id: string) {
  const repository = container.resolve(GuaranteeRepositoryToken);
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (rule: GuaranteeRule) => repository.updateRule(id, rule),
    // A régua pode ter travado entre abrir a tela e salvar (422): o detalhe
    // relido passa a mostrá-la travada.
    onSettled: invalidate,
  });
}
